import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useApiClient from '../hooks/useApiClient';
import { getUnitMeasurement } from '../components/dashboard/documents/ViewNIR';

const getDateParts = (dateStr) => {
  if (!dateStr) return { day: '', month: '', year: '' };
  const date = new Date(dateStr);
  return {
    day: String(date.getDate()).padStart(2, '0'),
    month: String(date.getMonth() + 1).padStart(2, '0'),
    year: date.getFullYear(),
  };
};

const PrintableMIS = () => {
  const [searchParams] = useSearchParams();
  const documentId = searchParams.get('id');
  const [misData, setMisData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const apiClient = useApiClient();

  useEffect(() => {
    const loadMisDocumentData = async () => {
      if (!documentId) {
        setError('Document ID is missing.');
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiClient.get(`/mis/${documentId}`);
        setMisData(response);
      } catch (err) {
        console.error('Error fetching MIS data:', err);
        setError(err.response?.data?.message || err.message || 'Failed to load MIS data.');
      }
      setIsLoading(false);
    };

    loadMisDocumentData();
  }, [documentId, apiClient]);

  // Using useMemo for derived data, similar to PrintableGRN if transformations are complex
  // For now, we'll access misData directly or make simple derivations in JSX

  if (isLoading) {
    return <div className="p-8 text-center">Loading document...</div>;
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <h1 className="text-xl font-bold text-red-600 mb-4">Error Loading MIS</h1>
        <p className="text-gray-700">{error}</p>
      </div>
    );
  }
  
  if (!misData) {
    return <div className="p-8 text-center">No data available for this document.</div>;
  }

  // Destructure from misData. companyName, predator, receiver might not be in the direct GET /mis/{id} response.
  // They will default to undefined, and the JSX will handle it with fallbacks.
  const { seriesNumber, date, items } = misData; 

  const dateParts = getDateParts(date);
  const totalValue = (items || []).reduce((sum, item) => sum + parseFloat(item.value || 0), 0).toFixed(2);

  return (
    <div className="printable-container text-gray-800 font-sans text-xs">
      <style>
        {`
          body {
            background-color: #f0f0f0;
          }
          .printable-container {
            width: 210mm;
            margin: 20px auto;
            padding: 15mm;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
            border: 1px solid #ccc;
            background: white;
            max-height: 90vh;
            overflow-y: auto;
            overflow-x: hidden;
          }
          .document-content {
            width: 100%;
          }
          .page-footer {
            width: 100%;
            background: #f8f9fa;
            border-top: 2px solid #2c3e50;
            border-bottom: 2px solid #2c3e50;
            padding: 10px 15px;
            box-sizing: border-box;
            margin-top: 30px;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              background-color: #fff !important;
              width: 100% !important;
              height: auto !important;
              overflow: visible !important;
              position: static !important;
            }
            a[href]:after {
              content: none !important;
            }
            .printable-container {
              width: 100%;
              margin: 0;
              padding: 10mm;
              box-shadow: none;
              border: none;
              max-height: none;
              overflow: visible;
            }
            .no-print {
              display: none !important;
            }
            .page-footer {
              padding: 10px;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            table {
              page-break-inside: auto;
            }
            tbody tr {
              page-break-inside: avoid;
              break-inside: avoid;
            }
            tfoot {
              display: table-footer-group;
            }
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }
          th, td {
            padding: 4px 6px;
            text-align: left;
          }
          .header-main {
            background-color: #2c3e50;
            color: white;
            padding: 15px;
            margin-bottom: 20px;
          }
          .header-main h1 {
            font-size: 1.8em;
            font-weight: bold;
            margin:0;
          }
          .header-main p {
            font-size: 0.9em;
            margin:0;
          }
          .details-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 20px;
            margin-bottom: 20px;
          }
          .details-box {
            border: 1px solid #333;
            padding: 12px;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .details-box h3 {
            font-weight: bold;
            margin-bottom: 5px;
            font-size: 0.9em;
            text-transform: uppercase;
          }
          .items-table th {
            border-bottom: 1px solid #555;
            font-weight: bold;
          }
          .items-table td {
            border-bottom: 1px dotted #ccc;
          }
          .items-table .text-right { text-align: right; }
          .total-row td {
            font-weight: bold;
            border-top: 2px solid #333;
            padding-top: 8px;
          }
          .items-table tbody tr {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .items-table tfoot {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .items-table tfoot tr {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .details-grid, .header-main {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .footer-notes {
            margin-top: 20px;
            font-size: 0.8em;
            color: #555;
          }
          .section-title {
            font-weight: bold;
            text-transform: uppercase;
            margin-top: 8px;
            margin-bottom: 4px;
            font-size: 0.9em;
          }
          .mb-4 {
            margin-bottom: 16px;
          }
        `}
      </style>

      <div className="no-print mb-6 flex gap-3">
        <button
          onClick={() => window.print()}
          className="p-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          Printează / Salvează PDF
        </button>
      </div>

      <div className="document-content">
        <div className="header-main">
          <h1>BON DE CONSUM</h1>
          <p className='py-2'>Data: {dateParts.day}/{dateParts.month}/{dateParts.year}</p>
          {seriesNumber && <p>Serie și număr: {seriesNumber}</p>}
        </div>

          <div className="details-grid">
            <div className="details-box">
              <h3>COMPANIE</h3>
              <p><strong>{misData.shopName || 'N/A'}</strong></p>
              {misData.tax_identifier && <p>CUI: {misData.tax_identifier}</p>}
              {misData.reg_com && <p>Reg. com.: {misData.reg_com}</p>}
              {misData.address && <p>Adresa: {misData.address}</p>}
            </div>
          </div>
          <h3 className="section-title">Materiale</h3>
          <table className="items-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Denumirea Materialelor (inclusiv sort, marca, profil, dimensiune)</th>
                <th className="text-right">Cant. necesară</th>
                <th>Cod</th>
                <th className="text-right">UM</th>
                <th className="text-right">Cant. eliberată</th>
                <th className="text-right">Preț Unitar</th>
                <th className="text-right">Valoarea</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={item.id || idx}>
                  <td>{idx + 1}</td>
                  <td>{item.productName || ''}</td>
                  <td className="text-right">{item.quantityConsumed || ''}</td>
                  <td className="text-center">{item.productIdentification || ''}</td>
                  <td className="text-center">{getUnitMeasurement(item.um) || ''}</td>
                  <td className="text-right">{item.quantityConsumed || ''}</td>
                  <td className="text-right">{item.unitPrice || ''}</td>
                  <td className="text-right">{item.value || ''}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan="8" className="text-center py-4">Nu sunt materiale disponibile.</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td colSpan="7" className="text-right">TOTAL</td>
                <td className="text-right">{totalValue}</td>
              </tr>
            </tfoot>
          </table>

        <div className="page-footer">
          <div className="flex justify-between" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <h3 style={{ fontWeight: 700, fontSize: '0.85em', color: '#2c3e50', marginBottom: 4 }}>Gestionar</h3>
              <p style={{ margin: 0, color: 'rgb(98,98,98)' }}>{misData.representative?.name || '_________________________'}</p>
            </div>
            <div>
              <h3 style={{ fontWeight: 700, fontSize: '0.85em', color: '#2c3e50', marginBottom: 4 }}>Beneficiar</h3>
              <p style={{ margin: 0, color: 'rgb(98,98,98)' }}>Semnătură: _________________________</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintableMIS; 